import React, { createContext, useContext, useMemo, useRef } from 'react';

interface ProfileEditorRegistry {
    /** Called by an editor when it opens, so Escape can reach it. */
    register: (id: string, close: () => void) => void;
    unregister: (id: string) => void;
    /** Closes every open editor. Returns false when there was none. */
    closeAll: () => boolean;
}

const ProfileEditorContext = createContext<ProfileEditorRegistry | null>(null);

/**
 * Lets the customize dialog know an inline profile editor is open.
 *
 * Radix registers its Escape handler on `document` with `{ capture: true }`
 * (`@radix-ui/react-use-escape-keydown`), so it runs before any handler inside
 * the editor — an editor can never stop the dialog from closing out from under
 * it, and the half-typed value would be lost. The dialog has to be the one that
 * declines, via `onEscapeKeyDown`, which is why the open editors are tracked up
 * here instead of purely in component state.
 */
export function useProfileEditorRegistry() {
    const openEditors = useRef(new Map<string, () => void>());

    return useMemo(
        () => ({
            register: (id: string, close: () => void) => {
                openEditors.current.set(id, close);
            },
            unregister: (id: string) => {
                openEditors.current.delete(id);
            },
            closeAll: () => {
                if (openEditors.current.size === 0) return false;

                openEditors.current.forEach((close) => close());
                openEditors.current.clear();
                return true;
            },
        }),
        [],
    );
}

export function ProfileEditorProvider({
    registry,
    children,
}: {
    registry: ProfileEditorRegistry;
    children: React.ReactNode;
}) {
    return (
        <ProfileEditorContext.Provider value={registry}>{children}</ProfileEditorContext.Provider>
    );
}

export const useProfileEditorContext = () => useContext(ProfileEditorContext);
